'use client'

import { Link } from 'react-router-dom'
import { Search, User, Sun, Moon, Menu, Sparkles } from 'lucide-react'
import { useState } from 'react'
import { useThemeStore } from '@/stores/theme'
import { useCurrencyStore, type Currency } from '@/stores/currency'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import CartDropdown from '@/components/dropdown/CartDropdown'
import WishlistDropdown from '@/components/dropdown/WishlistDropdown'
import CategoriesDropdown from '@/components/dropdown/CategoriesDropdown'
import NotificationDropdown from '@/components/notifications/NotificationDropdown'
import AIShoppingAssistant from '@/components/ai/AIShoppingAssistant'

export default function ECommerceHeader() {
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showAIAssistant, setShowAIAssistant] = useState(false)
  
  const { theme, setTheme } = useThemeStore()
  const { currency, setCurrency } = useCurrencyStore()

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light')
  }

  const currencies: Currency[] = ['BDT', 'USD', 'EUR']
  const currencySymbols = { BDT: '৳', USD: '$', EUR: '€' }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card shadow-sm">
      {/* Top Navigation Bar */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center gap-4">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <div className="text-2xl font-bold text-primary">
              <span className="text-brand-green">Bot</span>
              <span className="text-brand-orange">Tola</span>
            </div>
          </Link>

          {/* Search Bar */}
          <div className="flex-1 max-w-2xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search for products..."
                className="pl-10 w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {/* Currency Selector */}
            <select 
              value={currency}
              onChange={(e) => setCurrency(e.target.value as Currency)}
              className="hidden md:block px-2 py-1.5 text-sm font-medium border border-border rounded-md bg-background text-foreground cursor-pointer hover:bg-muted"
            >
              {currencies.map((curr) => (
                <option key={curr} value={curr}>
                  {curr} ({currencySymbols[curr]})
                </option>
              ))}
            </select>

            {/* Theme Toggle - Single Button */}
            <button
              onClick={toggleTheme}
              className="hidden md:flex items-center justify-center p-2 rounded-md border border-border bg-muted hover:bg-background transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <Sun className="h-4 w-4 text-foreground" />
              ) : (
                <Moon className="h-4 w-4 text-foreground" />
              )}
            </button>

            {/* Wishlist Dropdown */}
            <WishlistDropdown />

            {/* Notification Dropdown */}
            <NotificationDropdown />

            {/* AI Shopping Assistant Button */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAIAssistant(true)}
              className="hidden md:flex items-center gap-2 border-primary/30 hover:bg-primary/10"
            >
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="hidden lg:inline">AI Assistant</span>
            </Button>

            {/* Cart Dropdown */}
            <CartDropdown />

            {/* User Account */}
            <Link to="/account">
              <Button variant="ghost" size="sm">
                <User className="h-5 w-5" />
                <span className="hidden lg:inline ml-1">Account</span>
              </Button>
            </Link>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Navigation Menu */}
      <div className="border-t border-border bg-card">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-6 py-2 overflow-x-auto overflow-y-visible">
            {/* All Categories Dropdown */}
            <div className="relative z-50">
              <CategoriesDropdown />
            </div>

            <Link
              to="/flash-deals"
              className="text-sm font-medium text-accent hover:text-accent/80 whitespace-nowrap transition-colors"
            >
              Flash Deals
            </Link>
            <Link
              to="/new-arrivals"
              className="text-sm font-medium text-foreground hover:text-primary whitespace-nowrap transition-colors"
            >
              New Arrivals
            </Link>
            <Link
              to="/top-vendors"
              className="text-sm font-medium text-foreground hover:text-primary whitespace-nowrap transition-colors"
            >
              Top Vendors
            </Link>
            <Link
              to="/bottola-choice"
              className="text-sm font-medium text-primary hover:text-primary/80 whitespace-nowrap transition-colors"
            >
              BotTola Choice
            </Link>
          </nav>
        </div>
      </div>

      {/* AI Shopping Assistant Modal */}
      <AIShoppingAssistant open={showAIAssistant} onClose={() => setShowAIAssistant(false)} />
    </header>
  )
}
